import { createParser } from 'eventsource-parser';

import type { Message } from '../../types';
import { apiClient, getAuthHeader } from '../../apiClient';
import { retrieveAccessTokenFromBackend } from '../../securityService';
import { apiUrl } from '../../apiUrls';
import { captureError } from '../../core/errors/captureError';

// ── SSE event payloads ────────────────────────────────────────────────────

export type QuotaEvent = {
    threshold_fraction?: number;
    used: number;
    limit: number;
};

export type StreamErrorEvent = {
    message: string;
    code?: string;
    retriable?: boolean;
};

export type MessageStartEvent = {
    message_id: string;
    role: string;
    model: string;
    started_at: string;
};

export type ContentDeltaEvent = {
    message_id: string;
    delta: string;
    seq: number;
};

export type MessageCompleteEvent = {
    message_id: string;
    content: string;
    tool_calls: unknown[];
    usage: Record<string, number> | null;
    finish_reason: string;
    model: string;
    latency_ms: number;
};

export type StreamHandlers = {
    /** Full-thread snapshot — emitted at stream open and after each tool round. */
    onThreadSync?: (thread: Message[]) => void;
    /** Legacy full-thread replacement — kept for backward compat. */
    onMessagesUpdated?: (thread: Message[]) => void;
    /** A new assistant message started; insert a streaming placeholder. */
    onMessageStart?: (ev: MessageStartEvent) => void;
    /** A token arrived; append to the in-flight bubble. */
    onContentDelta?: (ev: ContentDeltaEvent) => void;
    /** Assistant turn complete; finalize the bubble. */
    onMessageComplete?: (ev: MessageCompleteEvent) => void;
    onQuotaWarning?: (ev: QuotaEvent) => void;
    onQuotaExhausted?: (ev: QuotaEvent) => void;
    onError?: (err: StreamErrorEvent) => void;
    onDone?: () => void;
};

// ── Transport ──────────────────────────────────────────────────────────────

async function openChatStream(
    chatId: string,
    body: { content: string; client_nonce: string; file?: File | null },
    signal: AbortSignal,
): Promise<Response> {
    const url = apiUrl(`/api/chats/${chatId}/messages/`);

    async function send(headers: HeadersInit): Promise<Response> {
        const finalHeaders = { ...headers } as Record<string, string>;
        let payload: BodyInit;
        if (body.file) {
            const formData = new FormData();
            formData.append('content', body.content);
            formData.append('client_nonce', body.client_nonce);
            formData.append('files', body.file);
            formData.append('tool_calls', JSON.stringify([]));
            formData.append('tool_call_id', '');
            payload = formData;
            delete finalHeaders['Content-Type'];
        } else {
            payload = JSON.stringify({
                content: body.content,
                client_nonce: body.client_nonce,
                tool_calls: [],
                tool_call_id: '',
            });
        }
        return fetch(url, {
            method: 'POST',
            headers: finalHeaders,
            credentials: 'include',
            body: payload,
            signal,
        });
    }

    const initialHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
    };

    let response = await send(initialHeaders);
    if (response.status === 401) {
        try {
            const fresh = await retrieveAccessTokenFromBackend();
            if (fresh) {
                response = await send({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${fresh}`,
                });
            }
        } catch (err) {
            captureError(err, { scope: 'chatservice', op: 'refreshAuthToken' });
        }
    }

    if (!response.ok) {
        if (response.status === 402) {
            let detail: Record<string, unknown> = {};
            try {
                detail = await response.json();
            } catch (err) {
                captureError(err, { scope: 'chatservice', op: 'parseQuotaErrorBody' });
            }
            const err = new Error(
                (detail.detail as string) || "You've used your free tokens this month.",
            );
            (err as Error & { code?: string; status?: number }).code = 'chat_quota_exhausted';
            (err as Error & { status?: number }).status = 402;
            throw err;
        }
        let detail: string | null = null;
        try {
            const body = await response.json();
            detail = typeof body?.detail === 'string' ? body.detail.trim() : null;
            if (!detail && typeof body?.message === 'string') detail = body.message.trim();
            if (!detail && typeof body?.error?.message === 'string') detail = body.error.message.trim();
        } catch (err) {
            captureError(err, { scope: 'chatservice', op: 'parseErrorBody' });
        }
        throw new Error(detail ?? 'Something went wrong. Please try again.');
    }
    if (!response.body) throw new Error('Response body is null');
    return response;
}

export async function getMessagesForChat(chatId: string): Promise<Message[]> {
    const response = await apiClient.get(`/api/chats/${chatId}/messages/`);
    return response.data;
}

export async function createMessage(chatId: string, content: string): Promise<Message> {
    const response = await apiClient.post(
        `/api/chats/${chatId}/messages/`,
        { content, tool_calls: [], tool_call_id: '' },
    );
    return response.data;
}

/** New SSOT transport. Posts a user message and dispatches typed SSE events
 *  to the handler callbacks. Callers are expected to wire the `AbortSignal`
 *  into an `AbortController` so `stop()` semantics reach the backend. */
/** Regenerate the last assistant turn. Returns the same SSE event stream
 *  as a normal send but against a server-pruned thread. Reuses the core
 *  dispatcher via a thin transport wrapper. */
export async function regenerateLastTurn(
    chatId: string,
    handlers: StreamHandlers,
    signal: AbortSignal,
): Promise<void> {
    const url = apiUrl(`/api/chats/${chatId}/messages/regenerate/`);
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeader()),
        },
        credentials: 'include',
        body: '{}',
        signal,
    });
    if (!response.ok) {
        if (response.status === 402) {
            const err = new Error("You've used your free tokens this month.") as Error & { code?: string };
            err.code = 'chat_quota_exhausted';
            throw err;
        }
        let detail: string | null = null;
        try {
            const body = await response.json();
            detail = typeof body?.detail === 'string' ? body.detail.trim() : null;
            if (!detail && typeof body?.message === 'string') detail = body.message.trim();
            if (!detail && typeof body?.error?.message === 'string') detail = body.error.message.trim();
        } catch (err) {
            captureError(err, { scope: 'chatservice', op: 'parseRegenerateErrorBody' });
        }
        throw new Error(detail ?? 'Something went wrong. Please try again.');
    }
    if (!response.body) throw new Error('Response body is null');
    await dispatchSSE(response, handlers);
}

async function dispatchSSE(response: Response, handlers: StreamHandlers): Promise<void> {
    const errorRef: { value: StreamErrorEvent | null } = { value: null };
    const parser = createParser({
        onEvent(ev) {
            let parsed: unknown = null;
            if (ev.data) {
                try {
                    parsed = JSON.parse(ev.data);
                } catch (err) {
                    captureError(err, { scope: 'chatservice', op: 'parseSseEvent' });
                    return;
                }
            }
            dispatchEvent(ev.event, parsed, handlers, errorRef);
        },
    });
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            parser.feed(decoder.decode(value, { stream: true }));
            if (errorRef.value) break;
        }
    } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
            const abortErr = new Error('Stream cancelled by user') as Error & { code?: string };
            abortErr.code = 'user_aborted';
            throw abortErr;
        }
        throw new Error('Connection lost while receiving response. Please try again.');
    } finally {
        try {
            reader.cancel();
        } catch (err) {
            captureError(err, { scope: 'chatservice', op: 'cancelStreamReader' });
        }
    }
    if (errorRef.value) {
        const sseError = errorRef.value;
        const err = new Error(sseError.message || 'Server error during streaming.') as
            Error & { code?: string; retriable?: boolean };
        if (sseError.code) err.code = sseError.code;
        err.retriable = sseError.retriable ?? false;
        throw err;
    }
}

function dispatchEvent(
    eventName: string | undefined,
    data: unknown,
    handlers: StreamHandlers,
    errorRef: { value: StreamErrorEvent | null },
): void {
    switch (eventName) {
        case 'thread_sync':      handlers.onThreadSync?.((data as { thread: Message[] }).thread); break;
        case 'messages_update':  handlers.onMessagesUpdated?.(data as Message[]); break;
        case 'message_start':    handlers.onMessageStart?.(data as MessageStartEvent); break;
        case 'content_delta':    handlers.onContentDelta?.(data as ContentDeltaEvent); break;
        case 'message_complete': handlers.onMessageComplete?.(data as MessageCompleteEvent); break;
        case 'quota_warning':    handlers.onQuotaWarning?.(data as QuotaEvent); break;
        case 'quota_exhausted':  handlers.onQuotaExhausted?.(data as QuotaEvent); break;
        case 'error':
            errorRef.value = data as StreamErrorEvent;
            handlers.onError?.(errorRef.value);
            break;
        case 'done':             handlers.onDone?.(); break;
        default: break;  // unknown event — forward-compat
    }
}

export async function streamChatResponse(
    chatId: string,
    body: { content: string; client_nonce: string; file?: File | null },
    handlers: StreamHandlers,
    signal: AbortSignal,
): Promise<void> {
    const response = await openChatStream(chatId, body, signal);
    await dispatchSSE(response, handlers);
}

/** 64-hex client nonce for idempotency. Falls back to Math.random in
 *  ancient browsers without crypto.getRandomValues (unlikely but safe). */
export function cryptoRandomNonce(): string {
    if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

export async function deleteChat(chatId: string): Promise<void> {
    await apiClient.delete(`/api/chats/${chatId}/`);
}

export async function renameChat(chatId: string, name: string): Promise<void> {
    await apiClient.patch(`/api/chats/${chatId}/`, { name });
}

export async function moveChatToProject(chatId: string, projectId: string | null): Promise<void> {
    await apiClient.patch(`/api/chats/${chatId}/`, { project: projectId || '' });
}
